import { ExperienceLevelEnum } from '../lib/constants/constants';
import { ApiResponseType } from './api';
import { GenderEnum, HighestEducationEnum, UserRoleEnum } from './auth';
import { RelationEnum, SortTypeEnum } from './index';
import { OfferReviewStateEnum, OfferStateEnum, OfferType, ReviewOfferType } from './offer';
import { CompanyReviewType } from './review';
import { SuspendEnum } from './suspension';

export enum OfferNextActionEnum {
    releaseAnOffer,
    reOffer,
    noActionAvailable,
}

export type CandidateAdvancedRequestType = {
    Start: number;
    Limit: number;
    'FirstName.FilterValue'?: string;
    'FirstName.SortType'?: SortTypeEnum;
    'LastName.FilterValue'?: string;
    'LastName.SortType'?: SortTypeEnum;
    'JobTitle.FilterValue'?: string;
    'JobTitle.SortType'?: SortTypeEnum;
    'CityDistrict.FilterValue'?: string;
    'CityDistrict.SortType'?: SortTypeEnum;
    'State.FilterValue'?: string;
    'State.SortType'?: SortTypeEnum;
    'InstitutionName.FilterValue'?: string;
    'InstitutionName.SortType'?: SortTypeEnum;
    'Email.FilterValue'?: string;
    'Email.SortType'?: SortTypeEnum;
    'Phone.FilterValue'?: string;
    'Phone.SortType'?: SortTypeEnum;
};

export type CandidateGlobalRequestType = {
    Start: number;
    Limit: number;
    'SearchParam.FilterValue'?: string;
    IsOfferRelease: boolean;
};

export type CandidateProfileType = {
    id: string;
    active: boolean;
    email: string;
    role: UserRoleEnum;
    firstName: string;
    middleName?: string;
    offerExists: boolean;
    lastName: string;
    avatarUrl: string;
    PostalCode: string;
    candidateAddress: string;
    flatAddress: string;
    streetAddress: string;
    bannerUrl: string;
    jobTitle: string;
    cityDistrict: string;
    professionalUrl: string;
    state: string;
    offerType: OfferType;
    website: string;
    highestEducation: HighestEducationEnum | null;
    institutionName: string | null;
    graduationYear: number;
    candidateReviews: CompanyReviewType[];
    reviewsCount: number;
    rating: {
        value: number;
    };
    contractComplianceQuantity: number;
    contractViolationQuantity: number;
    aboutMe: string | null;
    phone: string | null;
    phoneList: string[] | null;
    candidateNumber: number;
    resumeFileUrl: string;
    reOfferId: string | null;
    allCompaniesAcceptedOffersCount: number;
    currentCompanyLastEventShortText: string | null;
    currentCompanyLastEventDetailedText: string | null;
    currentCompanyOfferNextAvailableAction: OfferNextActionEnum;
    dob: Date;
    gender: GenderEnum;
    aadhar: string;
    country: string;
    relationType: RelationEnum;
    suspendStatus?: SuspendEnum;
    offerId?: string | null;
    offerReviewState: OfferReviewStateEnum;
    progressReviews: CompanyReviewType[];
    onboardingReviews: CompanyReviewType[];
    exitReviews: CompanyReviewType[];
    reviewOffers: ReviewOfferType[];
    prevCompany: string;
    experienceLevel: ExperienceLevelEnum;
    userCountryCode: string;
    profileCompletionPercentage?: number;
    offerDate?: Date | null;
    acceptedDate?: Date | null;
    jobStartDate?: Date | null;
    coePending: number;
    coeAccepted: number;
    coeRetracted: number;
    coeDeclined: number;
    isUser?: number;
    candidateProfileURL: string;
    companyProfileURL: string;
    companyStatus?: number;
    candidateStatus?: number;
    candidateType?: number;
};

export type CandidateOfferHistoryType = {
    jobTitle: string;
    offerAcceptedDate: Date;
    offerState: OfferStateEnum;
    offerType: number;
};

export type EditCandidateFormType = {
    firstName: string;
    middleName: string;
    lastName: string;
    avatarUrl: string;
    jobTitle: string;
    CityDistrict: string | null;
    state: string | null;
    aboutMe: string;
    website: string;
    phone: string;
    highestEducation: HighestEducationEnum | string | null;
    institutionName: string;
    graduationYear: string;
    dob: Date;
    gender: GenderEnum;
    // professionalUrl: string;
    CandidateAddress: string;
    aadhar: string;
    flatAddress: string;
    streetAddress: string;
    PostalCode: string;
    country: string;
    prevCompany: string;
    experienceLevel: ExperienceLevelEnum | string | null;
    offerDate?: Date | null | string;
    acceptedDate?: Date | null | string;
    jobStartDate?: Date | null | string;
    candidateId?: string;
};

export type UpdateCandidateRequestType = Omit<EditCandidateFormType, 'graduationYear' | 'prevCompany'> & {
    graduationYear: number;
    prevCompany: string | null;
};

export type CandidateListType = {
    id: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    jobTitle: string;
    country: string;
    cityDistrict: string;
    state: string;
    institutionName: string | null;
    avatarUrl: string | null;
    rating: number;
    reviewsCount: number;
    aboutMe: string | null;
    phone: string | null;
    email: string;
    website: string;
    highestEducation: HighestEducationEnum | null;
    graduationYear: number;
    allCompaniesAcceptedOffersCount: number;
    currentCompanyLastEventShortText: string | null;
    currentCompanyLastEventDetailedText: string | null;
    currentCompanyOfferNextAvailableAction: OfferNextActionEnum;
    coePending: number;
    coeAccepted: number;
    coeRetracted: number;
    coeDeclined: number;
    reOfferId: string | null;
    suspendStatus?: SuspendEnum;
    suspendedStatus?: SuspendEnum;
    progressReviews?: CompanyReviewType[];
    onboardingReviews?: CompanyReviewType[];
    exitReviews?: CompanyReviewType[];
    contractComplianceQuantity?: number;
    contractViolationQuantity?: number;
    experienceLevel: ExperienceLevelEnum;
    isUser?: number;
    gender?: number;
    candidateLocation?: string;
    candidateProfileURL?: string;
    companyProfileURL?: string;
};

export type CandidateListResponseType = Omit<ApiResponseType, 'resultObject'> & {
    resultObject: {
        items: CandidateListType[];
        hasMore: boolean;
        count: number;
    };
};

export type CandidateProfileResponseType = Omit<ApiResponseType, 'resultObject'> & {
    resultObject: CandidateProfileType;
};

export type CandidateOfferHistoryResponseType = Omit<ApiResponseType, 'resultObject'> & {
    resultObject: CandidateOfferHistoryType[];
};

export interface MarketPlaceCandidateFormData {
    FirstName: string;
    MiddleName: string;
    LastName: string;
    Email: string;
    CityDistrict: string | null;
    state: string | null;
    country: string;
    Dob: Date | null;
    Gender: number | undefined;
    Phone: string;
    PostalCode: string;
    Password?: string;
    CandidateAddress?: string;
    HighestEducation: string | number | null;
    recaptcha?: string;
    InstitutionName: string;
    GraduationYear: string | number;
    UserCountryCode: string;
}

export interface FormFieldProps {
    field: any;
    form: any;
    error?: string | boolean;
    values: MarketPlaceCandidateFormData;
    setFieldValue: (field: string, value: any) => void;
    setFieldTouched: (field: string) => void;
    disabled?: boolean;
}

export interface DropdownConfig {
    isStateValueAvailable: boolean;
    isCityValueAvailable: boolean;
    stateDisabled: boolean;
    cityDisabled: boolean;
    iso2CountryString: string;
    iso2StateString: string;
}
